import { JobType, useJobPoll } from 'dataQueries'
import { VisualStagingSettings, useVisualStagingProcess, useVisualStagingResult } from 'dataQueries/photoStudio.query'

import { JobStatus } from 'constants/API'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { logger } from 'utils/logging'
import { PhotoStudioGenerationStepId } from '../generationSteps'

interface UseVisualStagingProcessorProps {
  assignmentId: string
  sourceVisualFilename: string
  getLatestPreviewFilename: () => string | null
  onProgressUpdate?: (step: string) => void
  onComplete?: (resultUrl: string) => void
  onError?: (error: any) => void
}

/**
 * Reusable Visual Staging processor hook
 * Handles the complete flow from mask upload to result display
 */
export const useVisualStagingProcessor = ({
  assignmentId,
  sourceVisualFilename,
  getLatestPreviewFilename,
  onProgressUpdate,
  onComplete,
  onError
}: UseVisualStagingProcessorProps) => {
  const visualStagingProcess = useVisualStagingProcess()
  const visualStagingJobPoll = useJobPoll(JobType.VISUAL_STAGING)
  const visualStagingResult = useVisualStagingResult()
  const { t } = useTranslation(['photo_studio_editing'])

  const processVisualStaging = useCallback(async (
    maskFilename: string | undefined,
    settings: VisualStagingSettings,
    useTempBucket: boolean,
    previousJobId?: string
  ) => {
    let lastJobStatus: string | undefined
    let lastJobError: string | undefined
    try {
      onProgressUpdate?.(PhotoStudioGenerationStepId.START_PROCESSING)

      // Step 1: Start Visual Staging processing
      const currentPreview = getLatestPreviewFilename()
      const inputVisualFilename = currentPreview || sourceVisualFilename
      const processResponse = await visualStagingProcess.mutateAsync({
        assignmentId,
        ...((!previousJobId || !!useTempBucket) && { visualFilename: inputVisualFilename }),
        maskFilename,
        settings,
        ...(previousJobId && { reExecuteFromPreviousJob: previousJobId }),
        useTempBucket,
      })

      onProgressUpdate?.(PhotoStudioGenerationStepId.UPLOAD_MASK)

      // Step 2: Poll job until completion
      const jobResult = await visualStagingJobPoll.mutateAsync({
        jobId: processResponse.id
      })
      lastJobStatus = jobResult.status
      lastJobError = jobResult.error

      if (jobResult.status === JobStatus.FINISHED) {
        onProgressUpdate?.(PhotoStudioGenerationStepId.PROCESSING_COMPLETE)

        // Step 3: Get the AI result
        const aiResult = await visualStagingResult.refetchWithParams(assignmentId, processResponse.id)

        if (aiResult.signedURL) {
          onComplete?.(aiResult.signedURL)
          return {
            success: true,
            resultUrl: aiResult.signedURL,
            jobId: processResponse.id
          }
        } else {
          throw new Error('No AI result available')
        }
      } else if (jobResult.status === JobStatus.FAILED) {
        // Throw error for failed Visual Staging job - toast will be shown by controller's onError callback
        throw new Error(t('virtual_staging_error'))
      } else {
        throw new Error(`Visual Staging job failed with status: ${jobResult.status}`)
      }
    } catch (error) {
      logger.error('Visual Staging: Processing failed', error, {
        context: {
          component: 'PhotoStudio',
          action: 'visualStagingProcess',
          metadata: {
            assignmentId,
            sourceVisualFilename,
            jobStatus: lastJobStatus,
            jobError: lastJobError,
          }
        }
      })
      onError?.(error)
      return {
        success: false,
        error
      }
    }
  }, [
    assignmentId,
    sourceVisualFilename,
    getLatestPreviewFilename,
    visualStagingProcess,
    visualStagingJobPoll,
    visualStagingResult,
    onProgressUpdate,
    onComplete,
    onError,
    t
  ])

  return {
    processVisualStaging,
    isProcessing: visualStagingProcess.isPending || visualStagingJobPoll.isPending,
    error: visualStagingProcess.error || visualStagingJobPoll.error
  }
}
